import React, { useState, useCallback, useEffect, useRef } from 'react';

import './style.scss';
import LocateForm from "../components/LocateForm";
import Map from "../components/Map";
import SearchResults from "../components/SearchResults";


const App = () => {
    const [googleApis, setGoogleApis] = useState({
        map: null,
        places: null,
        autocomplete: null,
    });
    const [options, setOptions] = useState({
        center: {
            lat: 37.0902,
            lng: -95.7129
        },
        zoom: 4,
        title: 'Locate A CDCES',
    });
    const [members, setMembers] = useState([]);
    const [pendingRequests, setPendingRequests] = useState(0);
    const [pagination, setPagination] = useState({
        lastPage: 1,
        page: 1,
        pageSize: 50,
        totalCount: 0,
    });
    const [hasSearch, setHasSearch] = useState(false);
    const [hasFilters, setHasFilters] = useState(false);

    const mapRef = useRef(null);
    const locationInputRef = useRef(null);

    const [googleLoaded, setGoogleLoaded] = useState(false);

    useEffect(() => {
        if (window.google) {
            setGoogleLoaded(true);
        } else {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = `https://maps.google.com/maps/api/js?key=${window.cbdceEducationFinder.apiKey}&libraries=places`;
            const firstScript = document.getElementsByTagName('script')[0];
            firstScript.parentNode.insertBefore(script, firstScript);
            script.addEventListener('load', () => {
                setGoogleLoaded(true);
            });
        }
    }, [!window.google, !mapRef.current, !locationInputRef.current]);

    useEffect(() => {
        if (!googleLoaded || !mapRef.current || !locationInputRef.current) return;
        const map = new window.google.maps.Map(mapRef.current, {
            ...options,
            zoomControl: true,
            zoomControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_CENTER
            }
        });
        const places = new window.google.maps.places.PlacesService(map);
        const autocomplete = new window.google.maps.places.Autocomplete(locationInputRef.current, {
            types: ['geocode'],
            componentRestrictions: { country: 'us' }
        });

        setGoogleApis({
            map, places, autocomplete
        });
    }, [googleLoaded, mapRef.current, locationInputRef.current]);


    const updateOptions = useCallback((next) => {
        setOptions(prev => {
            if (googleApis.map) {
                if (prev.zoom !== next.zoom) {
                    googleApis.map.setZoom(next.zoom);
                }

                if (prev.lat !== next.lat || prev.lng !== next.lng) {
                    googleApis.map.setCenter({lat: next.lat, lng: next.lng});
                }
            }

            return { ...prev, ...next };
        });
    }, [googleApis.map]);

    const updatePagination = useCallback((newIndex) => {
        setPagination((prev) => ({ ...prev, page: newIndex }));
    }, []);

    const updateMembers = useCallback((membersData) => {
        const { lastPage, page, pageSize, totalCount, items } = membersData;
        const updatedMembers = items.map((member) => {
            try {
                const { lat, lng } = JSON.parse(member.geoLocation);

                if (typeof lat !== 'number' || typeof lng !== 'number') {
                    console.error('Invalid geolocation data for Member', member);
                    return null;
                }

                return {
                    ...member,
                    geoLocation: {lat, lng},
                    marker: null,
                };
            } catch (e) {
                console.error('failed to parse Member geolocation', member, e);
                return null;
            }
        }).filter(member => member !== null);

        setMembers(updatedMembers);
        setPagination({ lastPage, page, pageSize, totalCount });
    }, []);



    return (
        <div className="cbdce-education-finder-app">
            <div className="row">
                <div className="col-12 col-lg-4 col-xl-3">
                    <LocateForm
                        googleApis={googleApis}
                        options={options}
                        members={members}
                        pagination={pagination}
                        setPendingRequests={setPendingRequests}
                        setHasSearch={setHasSearch}
                        hasSearch={hasSearch}
                        hasFilters={hasFilters}
                        setHasFilters={setHasFilters}
                        locationInputRef={locationInputRef}
                        updateOptions={updateOptions}
                        updateMembers={updateMembers}
                        updatePagination={updatePagination}
                    />
                </div>
                <div className="col-12 col-lg-8 col-xl-9">
                    <Map
                        id="cbdce-education-finder-map"
                        googleApis={googleApis}
                        mapRef={mapRef}
                        members={members}
                    />

                    <SearchResults
                        results={members}
                        pagination={pagination}
                        hasFilters={hasFilters}
                        hasSearch={hasSearch}
                        isSearching={pendingRequests > 0}
                        updatePagination={updatePagination}
                    />
                </div>
            </div>
        </div>
    );
};

export default App;
